import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../Provider';
import { supabase } from '../data/supabaseClient';
import { FaArrowRight, FaArrowLeft, FaUser, FaCar, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaBox, FaTimes } from 'react-icons/fa';
import PostDetailsModal from '../PostDetailsModal';
import Spinner from '../common/Spinner';
import { useTranslation } from 'react-i18next';
import './MyActivePostsDropdown.css';

// Memoized skeleton component for better performance
const SkeletonCard = React.memo(() => (
  <div className="mapd-skeleton-card"></div>
));

const MyActivePostsDropdown = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const [myPosts, setMyPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Create a ref for the scroll container
  const scrollContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Memoized fetch function
  const fetchMyActiveItems = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch both posts and packages concurrently for better performance
      const [postsResponse, packagesResponse] = await Promise.all([
        supabase
          .from('posts')
          .select(`
            id,
            role,
            travel_date,
            travel_time,
            address_from_city,
            address_to_city,
            address_from_street,
            address_to_street,
            distance_km,
            travel_type,
            status
          `)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .eq('is_completed', false)
          .order('created_at', { ascending: false })
          .limit(10),
        
        supabase
          .from('packages')
          .select(`
            id,
            travel_date,
            travel_time,
            address_from_city,
            address_to_city,
            address_from_street,
            address_to_street,
            distance_km,
            travel_type,
            status
          `)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(10)
      ]);

      if (postsResponse.error) throw postsResponse.error;
      if (packagesResponse.error) throw packagesResponse.error;

      // Process and combine data
      const processedPosts = postsResponse.data.map(post => ({
        ...post,
        itemType: 'post',
        matchCount: 0
      }));
      
      const processedPackages = packagesResponse.data.map(pkg => ({
        ...pkg,
        role: 'Package',
        itemType: 'package',
        matchCount: 0
      }));
      
      const allItems = [...processedPosts, ...processedPackages].sort((a, b) => {
        return new Date(b.travel_date) - new Date(a.travel_date);
      });

      setMyPosts(allItems);
    } catch (error) {
      console.error("Error fetching my posts and packages:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch data when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchMyActiveItems();
    }
  }, [isOpen, fetchMyActiveItems]);

  // Close dropdown when clicking outside (but not when modal is open)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if modal is open - check for modal elements
      const isModalClick = event.target.closest('.modal-overlay') || 
                          event.target.closest('.modal-container') ||
                          event.target.closest('.pdm-post-details-content');
      
      if (isModalClick) {
        return; // Don't close dropdown when interacting with modal
      }
      
      // Check if click is on the My Posts button itself (to allow toggle behavior)
      // Look for the specific button by finding nav-item with FaClipboardList icon or "My Posts" label
      const clickedElement = event.target;
      const navItem = clickedElement.closest('.nav-item');
      
      // Check if this nav-item contains the My Posts button content
      if (navItem && (navItem.querySelector('.nav-label')?.textContent === 'My Posts' || 
                     navItem.title === 'My Active Posts')) {
        return; // Let the button handle the toggle
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when dropdown is open (but allow modal to manage it when modal is open)
  useEffect(() => {
    if (isOpen && !isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!isOpen) {
      document.body.style.overflow = '';
    }
    // When modal is open, let the Modal component manage body scroll

    return () => {
      // Only reset if dropdown is closing and modal is not open
      if (!isModalOpen) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, isModalOpen]);

  // Memoized scroll handlers
  const handleScrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -280, behavior: 'smooth' });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 280, behavior: 'smooth' });
    }
  }, []);

  // Memoized modal handlers
  const handleItemClick = useCallback((item) => {
    setSelectedPost(item);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPost(null);
    // Don't close the dropdown - let user browse multiple posts
  }, []);

  // Memoized date formatter
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }, []);

  // Memoized time formatter - show only hours and minutes
  const formatTime = useCallback((timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  }, []);

  // Skeleton loader
  const renderSkeleton = () => (
    <div className="mapd-skeleton">
      {[1, 2, 3].map((index) => (
        <SkeletonCard key={`skeleton-${index}`} />
      ))}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop/Overlay */}
      <div className={`mapd-backdrop ${isOpen ? 'visible' : ''}`} />
      
      {/* Dropdown Container */}
      <div 
        ref={dropdownRef}
        className={`mapd-dropdown-container ${isOpen ? 'visible' : ''}`}
      >
        <div className="mapd-dropdown-content">
          {/* Header */}
          <div className="mapd-header">
            <h3>{t('posts.my_posts')}</h3>
          </div>

          {/* Content */}
          {isLoading ? (
            renderSkeleton()
          ) : error ? (
            <div className="mapd-error">
              <p>{t('errors.post_load_error')}: {error}</p>
            </div>
          ) : myPosts.length === 0 ? (
            <div className="mapd-empty">
              <p>{t('posts.no_active_posts')}</p>
            </div>
          ) : (
            <>
              {/* Scroll Controls */}
              {myPosts.length > 1 && (
                <div className="mapd-scroll-controls">
                  <button 
                    className="mapd-scroll-button left" 
                    onClick={handleScrollLeft}
                    aria-label="Scroll left"
                  >
                    <FaArrowLeft />
                  </button>
                  <button 
                    className="mapd-scroll-button right" 
                    onClick={handleScrollRight}
                    aria-label="Scroll right"
                  >
                    <FaArrowRight />
                  </button>
                </div>
              )}

              {/* Posts Scroll Container */}
              <div className="mapd-scroll-container" ref={scrollContainerRef}>
                {myPosts.map(post => (
                  <div key={post.id} className='mapd-post-card-frame'>
                    <div 
                      className="mapd-post-card" 
                      onClick={() => handleItemClick(post)}
                    >
                      <div className="mapd-role-indicator">
                        {post.role === "Driver" ? (
                          <FaCar className="mapd-role-icon driver" />
                        ) : post.role === "Passenger" ? (
                          <FaUser className="mapd-role-icon passenger" />
                        ) : (
                          <FaBox className="mapd-role-icon package" />
                        )}
                      </div>
                      
                      <div className="mapd-post-details">
                        <div className="mapd-post-locations">
                          <div className="mapd-post-location from">
                            <FaMapMarkerAlt className="mapd-location-icon" />
                            <span className="mapd-location-text">
                              {post.address_from_city || post.address_from_street}
                            </span>
                          </div>
                          <div className="mapd-post-connector"></div>
                          <div className="mapd-post-location to">
                            <FaMapMarkerAlt className="mapd-location-icon" />
                            <span className="mapd-location-text">
                              {post.address_to_city || post.address_to_street}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mapd-post-meta">
                          <div className="mapd-post-time">
                            <FaCalendarAlt className="mapd-meta-icon" />
                            <span>{formatDate(post.travel_date)}</span>
                          </div>
                          <div className="mapd-post-time">
                            <FaClock className="mapd-meta-icon" />
                            <span>{formatTime(post.travel_time)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Post Details Modal */}
      {isModalOpen && selectedPost && (
        <PostDetailsModal
          isOpen={isModalOpen}
          postId={selectedPost.id}
          postType={selectedPost.itemType || 'post'}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default MyActivePostsDropdown;
